import { useState } from 'react';
import { Image as ImageIcon, Plus, X, Youtube } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ImageUpload } from '@/components/ImageUpload';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/misc';
import type { MediaItem } from '@/types/content';

/**
 * One "Media" box for the Create and Edit pages (VAP-824).
 *
 * Writes to the card's mediaUrls list, which is the list the mobile app
 * renders. An image can be uploaded (presigned S3 PUT) or pasted as a URL.
 * A YouTube link is a media item of type 'youtube'.
 */
interface MediaEditorProps {
  mediaUrls: MediaItem[];
  onChange: (mediaUrls: MediaItem[]) => void;
  /** Present on the Edit page. Absent on Create: uploads land under pending/ until the card is saved. */
  cardId?: string;
}

type MediaInputMode = 'upload' | 'image' | 'youtube';

export function MediaEditor({ mediaUrls, onChange, cardId }: MediaEditorProps) {
  const [mode, setMode] = useState<MediaInputMode>('upload');
  const [newUrl, setNewUrl] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);

  const addUrl = () => {
    const url = newUrl.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) {
      setUrlError('The URL must start with http:// or https://');
      return;
    }
    if (mode === 'youtube' && !/youtube\.com|youtu\.be/i.test(url)) {
      setUrlError('Enter a youtube.com or youtu.be link');
      return;
    }
    onChange([...mediaUrls, { type: mode === 'youtube' ? 'youtube' : 'image', url }]);
    setNewUrl('');
    setUrlError(null);
  };

  const removeAt = (index: number) => {
    onChange(mediaUrls.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      {mediaUrls.length > 0 && (
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {mediaUrls.map((media, index) => (
            <li key={`${media.url}-${index}`} className="flex items-center gap-3 rounded-md border border-border bg-muted/40 p-2">
              {media.type === 'image' ? (
                <img
                  src={media.url}
                  alt=""
                  className="h-14 w-14 shrink-0 rounded object-cover bg-muted"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
                />
              ) : (
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded bg-red-500/10">
                  <Youtube className="h-6 w-6 text-red-500" />
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className="block text-[11px] uppercase tracking-wide text-muted-foreground">{media.type === 'image' ? 'Image' : 'YouTube'}</span>
                <span className="block truncate text-sm" title={media.url}>{media.url.split('/').pop() || media.url}</span>
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => removeAt(index)}
                aria-label={`Remove ${media.type === 'image' ? 'image' : 'video'}`}
                className="text-muted-foreground hover:text-destructive"
              >
                <X />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-3">
        <Tabs value={mode} onValueChange={(v) => { setMode(v as MediaInputMode); setUrlError(null); }}>
          <TabsList aria-label="Add media">
            <TabsTrigger value="upload">Upload image</TabsTrigger>
            <TabsTrigger value="image">Image URL</TabsTrigger>
            <TabsTrigger value="youtube">YouTube</TabsTrigger>
          </TabsList>
        </Tabs>

        {mode === 'upload' ? (
          // The list above shows the uploaded images, so no currentImageUrl is passed here.
          <ImageUpload
            cardId={cardId}
            onImageChange={(url) => {
              if (url) onChange([...mediaUrls, { type: 'image', url }]);
            }}
          />
        ) : (
          <div className="flex gap-2">
            <div className="relative flex-1">
              {mode === 'image' ? (
                <ImageIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
              ) : (
                <Youtube className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-red-500" aria-hidden />
              )}
              <Input
                type="url"
                value={newUrl}
                onChange={(e) => { setNewUrl(e.target.value); setUrlError(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addUrl(); } }}
                placeholder={mode === 'image' ? 'https://example.com/photo.jpg' : 'https://www.youtube.com/watch?v=...'}
                aria-label={mode === 'image' ? 'Image URL' : 'YouTube URL'}
                className={cn('pl-9', urlError && 'border-destructive')}
              />
            </div>
            <Button type="button" variant="outline" onClick={addUrl} disabled={!newUrl.trim()} aria-label="Add media">
              <Plus />
              Add
            </Button>
          </div>
        )}

        {urlError && <p className="text-sm text-destructive">{urlError}</p>}
      </div>
    </div>
  );
}
